// CheckboxList.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './CheckboxList.css';

// Components
import Checkbox from 'components/checkbox/Checkbox';

/**
 * CheckboxList
 *
 * Renders a list of checkbox items for single-select or multi-select usage.
 *
 * @param {Array<{ value: string, label: string, disabled?: boolean }>} options
 * @param {string} value - Selected value (single-select mode)
 * @param {Array<string>} values - Selected values (multi-select mode)
 * @param {boolean} multiSelect - Enables multi-select behavior
 * @param {function} onChange - Change handler
 * @param {boolean} disabled - Whether the entire list is disabled
 *
 * @returns {ReactNode}
 */
const CheckboxList = React.memo(({
    options = [],
    value,
    values,
    checkboxSize = 24,
    fontSize = "12px",
    onChange,
    disabled = false,
    multiSelect = false,
    className = '',
    style,
}) => {

    // ----- CONTEXT ----- //

    // Theme

    const { theme } = useTheme();

    const safeOptions = Array.isArray(options) ? options : [];

    const selectedValues = Array.isArray(values) ? values : [];
    const containerClassName = ['checkbox-list-container', className].filter(Boolean).join(' ');
    // ----- RENDER ----- //

    return (
        <div className={containerClassName} style={style}>
            {safeOptions.map((option) => {
                if (!option) return null;
                const optionValue = option.value ?? option.key ?? '';
                if (optionValue === '') return null;

                const label = option.label ?? String(optionValue);
                const isDisabled = disabled || Boolean(option.disabled);
                const isChecked = multiSelect
                    ? selectedValues.includes(optionValue)
                    : value === optionValue;

                return (
                    <div
                      className={`checkbox-list-item${isDisabled ? ' disabled' : ''}`}
                      key={optionValue}
                    >
                        <Checkbox
                          value={isChecked}
                          label={label}
                          size={checkboxSize}
                          fontSize={fontSize}
                          color={isDisabled ? theme.foregroundMuted : undefined}
                          checkedColor={isDisabled ? theme.foregroundMuted : undefined}
                          onChange={() => {
                                if (isDisabled) return;
                                if (!onChange) return;

                                if (multiSelect) {
                                    const nextValues = isChecked
                                        ? selectedValues.filter((entry) => entry !== optionValue)
                                        : [...selectedValues, optionValue];
                                    onChange(nextValues, optionValue);
                                    return;
                                }

                                onChange(optionValue);
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
});

// Dev Tools
CheckboxList.displayName = 'CheckboxList';

export default CheckboxList;
